// weatherMetarPills.ts
// ─────────────────────────────────────────────────────────────────────────────
// Observed-temperature pills: real METAR station observations rendered as
// rounded dark pills over the map — temperature on top, station name beneath.
//
//   ┌──────────────┐
//   │     76°      │
//   │  ORLANDO INTL│
//   └──────────────┘
//
// DATA: our Worker's /metars route (see metarsWorkerRoute.ts), which proxies
// + caches + slims the NOAA Aviation Weather Center feed. These are ACTUAL
// thermometer readings, unlike the model-sampled values — use these for the
// "now" state, and (optionally) model sampling for forecast frames.
//
// RENDERING: a MapLibre symbol layer with a 9-slice stretchable pill image
// (icon-text-fit: "both"), so the pill background stretches to fit each
// station's text. Everything stays GPU-rendered and collision-managed —
// hundreds of stations cost nothing, and MapLibre automatically shows only
// the pills that fit at the current zoom, biggest airports first.
//
// LIFECYCLE (all driven by the app):
//   attachMetarPills(map, getToken)   once after map load (and after each
//                                     styleEpoch bump — safe to re-call)
//   setMetarPillsEnabled(map, bool)   when the temperature-family layer is
//                                     (de)activated
//   setMetarPillsMode(map, key)       "temperature" | "dewpoint" |
//                                     "apparent_temperature" — which value
//                                     the pills display
//   setMetarPillsVisible(map, bool)   cheap visibility flip for forecast
//                                     scrubbing (keeps data, hides pills)
//   detachMetarPills(map)             full teardown (layer switch away)
//
// The module refetches on: enable, viewport moving outside the last-fetched
// (expanded) bbox, and a 5-minute staleness timer. All fetches are debounced
// and deduped.

// ─── Public IDs ─────────────────────────────────────────────────────────────

export const METAR_SOURCE = "metar-pills-src";
export const METAR_LAYER = "metar-pills";           // temperature boxes
const TEMP_BOX_IMAGE_ID = "metar-temp-box-bg";

// ─── Module state ───────────────────────────────────────────────────────────

type Mode = "temperature" | "dewpoint" | "apparent_temperature";

interface State {
  enabled: boolean;
  visible: boolean;
  mode: Mode;
  getToken: (() => Promise<string>) | null;
  apiBase: string;
  // Raw features as returned by the worker (values in °C / knots). We convert
  // per-mode at render time so switching temperature↔dewpoint↔feels-like is a
  // pure re-render with no refetch.
  features: any[];
  fetchedBbox: [number, number, number, number] | null; // expanded bbox covered
  fetchedAt: number;         // performance timestamp of last successful fetch
  inflight: AbortController | null;
  moveHandler: (() => void) | null;
  refreshTimer: ReturnType<typeof setInterval> | null;
  debounce: ReturnType<typeof setTimeout> | null;
}

const S: State = {
  enabled: false,
  visible: true,
  mode: "temperature",
  getToken: null,
  apiBase: "",
  features: [],
  fetchedBbox: null,
  fetchedAt: 0,
  inflight: null,
  moveHandler: null,
  refreshTimer: null,
  debounce: null,
};

const REFRESH_MS = 5 * 60 * 1000;   // obs cadence is ~hourly; 5 min is generous
const MOVE_DEBOUNCE_MS = 400;
// How far beyond the viewport to fetch, as a multiple of the viewport span.
// 0.5 = half a screen of margin each side, so small pans don't refetch.
const BBOX_MARGIN = 0.5;

// ─── Anchor stations ────────────────────────────────────────────────────────
// AWC priority alone can't break ties: the Northeast corridor packs ~8
// stations at prior 0 (KBOS/KJFK/KLGA/KEWR/KPHL/KBWI/KIAD/KDCA), and when
// sort keys tie MapLibre falls back to feature order — effectively arbitrary —
// so at national zoom "Boston" can lose its contested spot to a tie-break.
// Anchors get a sort boost so the station a viewer expects to see always wins
// placement against equal-priority neighbors. Rank within the list matters:
// earlier = stronger claim (beats later anchors when two anchors collide,
// e.g. DC beats Baltimore, JFK beats Newark).
const ANCHOR_ICAOS = [
  // US
  "KJFK", "KLAX", "KORD", "KATL", "KDCA", "KBOS", "KPHL", "KMIA", "KIAH",
  "KDFW", "KDEN", "KSEA", "KSFO", "KPHX", "KLAS", "KMSP", "KDTW", "KSTL",
  "KMCI", "KBNA", "KMSY", "KSLC", "KPDX", "KSAN", "KTPA", "KMCO", "KCLT",
  "KPIT", "KCLE", "KIND", "KCVG", "KMKE", "KOKC", "KABQ", "KSAT", "KAUS",
  "KMEM", "KJAX", "KBUF", "KRDU", "KBHM", "KLIT", "KDSM", "KOMA", "KICT",
  "KBIL", "KBOI", "KFAR", "KSUX", "KELP", "KANC", "PHNL",
  // Canada / Mexico / Caribbean
  "CYYZ", "CYVR", "CYUL", "CYYC", "CYWG", "CYOW", "CYHZ", "MMMX", "MMMY",
  "MUHA", "TJSJ",
  // Europe
  "EGLL", "LFPG", "EDDF", "EHAM", "LEMD", "LIRF", "EDDB", "LOWW", "EPWA",
  "EKCH", "ENGM", "ESSA", "EFHK", "LSZH", "EIDW", "LPPT", "LGAV", "UUEE",
  // Asia / Oceania / South America / Africa
  "RJTT", "RKSI", "ZBAA", "ZSPD", "VHHH", "RCTP", "VTBS", "WSSS", "WMKK",
  "VIDP", "VABB", "OMDB", "OTHH", "LTFM", "YSSY", "YMML", "NZAA", "SBGR",
  "SAEZ", "SCEL", "SKBO", "SPJC", "FAOR", "HECA", "DNMM", "GMMN",
];
const ANCHOR_RANK = new Map(ANCHOR_ICAOS.map((id, i) => [id, i]));

/**
 * Composite placement key: anchors first (in list order), then everything
 * else by AWC priority. Lower = placed earlier = wins contested spots.
 */
function sortRank(props: any): number {
  const anchor = ANCHOR_RANK.get(props.id);
  if (anchor !== undefined) return anchor;             // 0..~120
  return 1000 + (typeof props.pr === "number" ? props.pr : 9) * 100;
}

// ─── Unit + derived-value helpers ───────────────────────────────────────────

const c2f = (c: number) => c * 9 / 5 + 32;
const kt2mph = (kt: number) => kt * 1.15078;

/** Relative humidity (%) from temp + dewpoint via Magnus formula. */
function relHumidity(tempC: number, dewpC: number): number {
  const es = (t: number) => 6.112 * Math.exp((17.67 * t) / (t + 243.5));
  return Math.max(0, Math.min(100, 100 * es(dewpC) / es(tempC)));
}

/**
 * NWS "feels like": wind chill below 50°F with wind ≥ 3 mph, Rothfusz heat
 * index above 80°F, otherwise the air temperature. Inputs °C / knots, output °F.
 */
function feelsLikeF(tempC: number, dewpC: number | null, windKt: number | null): number {
  const tF = c2f(tempC);
  const vMph = windKt !== null ? kt2mph(windKt) : 0;

  if (tF <= 50 && vMph >= 3) {
    const v16 = Math.pow(vMph, 0.16);
    return 35.74 + 0.6215 * tF - 35.75 * v16 + 0.4275 * tF * v16;
  }
  if (tF >= 80 && dewpC !== null) {
    const rh = relHumidity(tempC, dewpC);
    let hi = -42.379 + 2.04901523 * tF + 10.14333127 * rh
      - 0.22475541 * tF * rh - 0.00683783 * tF * tF
      - 0.05481717 * rh * rh + 0.00122874 * tF * tF * rh
      + 0.00085282 * tF * rh * rh - 0.00000199 * tF * tF * rh * rh;
    // NWS low-RH / high-RH adjustments
    if (rh < 13 && tF >= 80 && tF <= 112) {
      hi -= ((13 - rh) / 4) * Math.sqrt((17 - Math.abs(tF - 95)) / 17);
    } else if (rh > 85 && tF >= 80 && tF <= 87) {
      hi += ((rh - 85) / 10) * ((87 - tF) / 5);
    }
    return hi;
  }
  return tF;
}

/** The displayed °F integer for a feature under the current mode, or null. */
function valueFor(props: any, mode: Mode): number | null {
  const tc = props.tc;
  if (typeof tc !== "number") return null;
  switch (mode) {
    case "dewpoint":
      return typeof props.dc === "number" ? Math.round(c2f(props.dc)) : null;
    case "apparent_temperature":
      return Math.round(feelsLikeF(tc, props.dc ?? null, props.wk ?? null));
    default:
      return Math.round(c2f(tc));
  }
}

// ─── Badge images ───────────────────────────────────────────────────────────
// A FIXED-SIZE rounded square holding just the temperature — no city name.
// Uniform boxes across the whole map, shown only when the timeline playhead
// sits on the "now" frame (past/forecast frames rely on the contour colors).

// Reference text size the temp box is drawn for — must equal the PEAK of the
// badge layer's text-size ramp. The icon-size expression divides by this.
const REF_TEXT_SIZE = 19;

/** Fixed rounded square for the temperature. Sized for the worst case
 *  ("104" / "-40" = 3-4 glyphs at REF_TEXT_SIZE) — never resizes. */
function buildTempBoxImage(): { data: ImageData; options: any } {
  const ratio = 2;
  const W = 46, H = 40, rad = 11;
  const w = W * ratio, h = H * ratio, r = rad * ratio;
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext("2d")!;
  ctx.clearRect(0, 0, w, h);
  ctx.beginPath();
  ctx.moveTo(r, 0);
  ctx.arcTo(w, 0, w, h, r);
  ctx.arcTo(w, h, 0, h, r);
  ctx.arcTo(0, h, 0, 0, r);
  ctx.arcTo(0, 0, w, 0, r);
  ctx.closePath();
  ctx.fillStyle = "rgba(10, 12, 16, 0.92)";
  ctx.fill();
  const data = ctx.getImageData(0, 0, w, h);
  // Fixed size — no stretch zones, no content box.
  return { data, options: { pixelRatio: ratio } };
}

// ─── Render (source/layer upsert) ───────────────────────────────────────────

function render(map: any): void {
  if (!map || !map.getStyle) return;

  // Build the display GeoJSON for the current mode. No truncation — names
  // live in their own auto-sized pill.
  const features = S.features
    .map((f) => {
      const v = valueFor(f.properties, S.mode);
      if (v === null) return null;
      return {
        type: "Feature",
        geometry: f.geometry,
        properties: {
          id: f.properties.id, // kept for debugging: inspect via querySourceFeatures
          label: String(v),
          rank: sortRank(f.properties),
        },
      };
    })
    .filter(Boolean);

  const geojson = { type: "FeatureCollection", features };

  // Badge images (setStyle wipes images — re-add when missing).
  if (!map.hasImage || !map.hasImage(TEMP_BOX_IMAGE_ID)) {
    try {
      const box = buildTempBoxImage();
      map.addImage(TEMP_BOX_IMAGE_ID, box.data, box.options);
    } catch { /* race with another render */ }
  }
  const src = map.getSource(METAR_SOURCE);
  if (src) {
    src.setData(geojson);
  } else {
    map.addSource(METAR_SOURCE, { type: "geojson", data: geojson });
  }

  // ── Temperature boxes (top layer, primary collision).
  if (!map.getLayer(METAR_LAYER)) {
    map.addLayer({
      id: METAR_LAYER,
      type: "symbol",
      source: METAR_SOURCE,
      layout: {
        // Fixed-size box: no icon-text-fit. The icon scales with a zoom ramp
        // mirroring text-size / REF_TEXT_SIZE so box and digits stay locked
        // in proportion — every badge identical regardless of the value.
        "icon-image": TEMP_BOX_IMAGE_ID,
        "icon-size": [
          "interpolate", ["linear"], ["zoom"],
          3, 12 / REF_TEXT_SIZE,
          6, 15 / REF_TEXT_SIZE,
          9, 19 / REF_TEXT_SIZE,
          12, 17 / REF_TEXT_SIZE,
        ],
        "icon-allow-overlap": false,
        "text-field": ["get", "label"],
        "text-font": ["Noto Sans Bold"],
        "text-size": [
          "interpolate", ["linear"], ["zoom"],
          3, 12,
          6, 15,
          9, 19,
          12, 17,
        ],
        "text-anchor": "center",
        "text-justify": "center",
        "text-allow-overlap": false,
        // Collision breathing room around the box; the name pill below is
        // covered by the box's padding at these ratios.
        "text-padding": [
          "interpolate", ["linear"], ["zoom"],
          3, 22,
          6, 14,
          9, 8,
          12, 4,
        ],
        "symbol-sort-key": ["get", "rank"],
        "visibility": S.visible ? "visible" : "none",
      },
      paint: {
        "text-color": "#ffffff",
        "text-halo-width": 0,
      },
    });
  }
}

// ─── Fetching ───────────────────────────────────────────────────────────────

function expandedViewportBbox(map: any): [number, number, number, number] {
  const b = map.getBounds();
  const w = b.getEast() - b.getWest();
  const h = b.getNorth() - b.getSouth();
  return [
    Math.max(-180, b.getWest() - w * BBOX_MARGIN),
    Math.max(-85, b.getSouth() - h * BBOX_MARGIN),
    Math.min(180, b.getEast() + w * BBOX_MARGIN),
    Math.min(85, b.getNorth() + h * BBOX_MARGIN),
  ];
}

function bboxContains(outer: number[] | null, inner: number[]): boolean {
  if (!outer) return false;
  return inner[0] >= outer[0] && inner[1] >= outer[1]
      && inner[2] <= outer[2] && inner[3] <= outer[3];
}

async function fetchMetars(map: any, force = false): Promise<void> {
  if (!S.enabled || !S.getToken || !map) return;

  // Skip when the current view is already covered by fresh-enough data.
  const view: [number, number, number, number] = [
    map.getBounds().getWest(), map.getBounds().getSouth(),
    map.getBounds().getEast(), map.getBounds().getNorth(),
  ];
  const fresh = performance.now() - S.fetchedAt < REFRESH_MS;
  if (!force && fresh && bboxContains(S.fetchedBbox, view)) return;

  // One in-flight fetch at a time; newest wins.
  S.inflight?.abort();
  const ctl = new AbortController();
  S.inflight = ctl;

  const bbox = expandedViewportBbox(map);
  try {
    const token = await S.getToken();
    const u = new URL(`${S.apiBase}/metars`);
    u.searchParams.set("bbox", bbox.join(","));
    u.searchParams.set("t", token);
    const res = await fetch(u.toString(), { signal: ctl.signal, cache: "no-store" });
    if (!res.ok) throw new Error(`metars ${res.status}`);
    const gj = await res.json();

    S.features = Array.isArray(gj?.features) ? gj.features : [];
    S.fetchedBbox = bbox;
    S.fetchedAt = performance.now();
    render(map);
  } catch (err: any) {
    if (err?.name === "AbortError") return;
    console.warn("[metarPills] fetch failed:", err?.message ?? err);
  } finally {
    if (S.inflight === ctl) S.inflight = null;
  }
}

// ─── Public API ─────────────────────────────────────────────────────────────

/**
 * One-time wiring: remembers the token minting fn + API base and installs the
 * moveend listener + refresh timer. Safe to call again after a styleEpoch
 * bump — it re-renders (setStyle wiped the source/layer/image) without
 * duplicating listeners.
 */
export function attachMetarPills(
  map: any,
  apiBase: string,
  getToken: () => Promise<string>,
): void {
  S.getToken = getToken;
  S.apiBase = apiBase;

  if (!S.moveHandler) {
    S.moveHandler = () => {
      if (!S.enabled) return;
      if (S.debounce) clearTimeout(S.debounce);
      S.debounce = setTimeout(() => fetchMetars(map), MOVE_DEBOUNCE_MS);
    };
    map.on("moveend", S.moveHandler);
  }
  if (!S.refreshTimer) {
    S.refreshTimer = setInterval(() => {
      if (S.enabled) fetchMetars(map, true);
    }, REFRESH_MS);
  }

  // Style swap resilience: if we have data but the layer was wiped, re-render.
  if (S.enabled && S.features.length > 0 && !map.getLayer(METAR_LAYER)) {
    render(map);
  }
}

/** Turn the pills on/off with the temperature-family layer. */
export function setMetarPillsEnabled(map: any, enabled: boolean): void {
  if (S.enabled === enabled) return;
  S.enabled = enabled;
  if (enabled) {
    fetchMetars(map, true);
  } else {
    removeLayerAndSource(map);
  }
}

/** Which observed value the pills display. Re-render only, no refetch. */
export function setMetarPillsMode(map: any, mode: Mode): void {
  if (S.mode === mode) return;
  S.mode = mode;
  if (S.enabled && S.features.length > 0) render(map);
}

/**
 * Cheap show/hide for timeline scrubbing: observations only exist at "now",
 * so hide (visibility, not teardown) while the playhead sits on forecast
 * frames, and flip back instantly when it returns to the present.
 */
export function setMetarPillsVisible(map: any, visible: boolean): void {
  if (S.visible === visible) return;
  S.visible = visible;
  try {
    if (map.getLayer(METAR_LAYER)) {
      map.setLayoutProperty(METAR_LAYER, "visibility", visible ? "visible" : "none");
    }
  } catch { /* mid-restyle — render() will honor S.visible */ }
}

/** Full teardown — call when leaving the Air section entirely. */
export function detachMetarPills(map: any): void {
  S.enabled = false;
  S.inflight?.abort();
  S.inflight = null;
  if (S.debounce) { clearTimeout(S.debounce); S.debounce = null; }
  if (S.refreshTimer) { clearInterval(S.refreshTimer); S.refreshTimer = null; }
  if (S.moveHandler && map?.off) { map.off("moveend", S.moveHandler); S.moveHandler = null; }
  S.features = [];
  S.fetchedBbox = null;
  removeLayerAndSource(map);
}

function removeLayerAndSource(map: any): void {
  if (!map) return;
  try {
    if (map.getLayer(METAR_LAYER)) map.removeLayer(METAR_LAYER);
    if (map.getSource(METAR_SOURCE)) map.removeSource(METAR_SOURCE);
  } catch { /* style swap mid-flight */ }
}